import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { onboardingApi } from '../api/onboarding.api'
import { step1Schema, type Step1FormData } from '../types'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Alert } from '@/components/ui/Alert'
import { useTranslation } from 'react-i18next'

interface OnboardingStep1Props {
  onNext: () => void
}

const LANGUAGES = [
  { value: 'fr', label: 'Français' },
  { value: 'en', label: 'English' },
  { value: 'ar', label: 'العربية' },
  { value: 'es', label: 'Español' },
]

export function OnboardingStep1({ onNext }: OnboardingStep1Props) {
  const { t } = useTranslation()
  const [apiError, setApiError] = useState<string | null>(null)
  const today = new Date().toISOString().split('T')[0]

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Step1FormData>({
    resolver: zodResolver(step1Schema),
    defaultValues: { language: 'fr' },
  })

  const onSubmit = async (data: Step1FormData) => {
    setApiError(null)
    try {
      await onboardingApi.postStep1(data)
      onNext()
    } catch {
      setApiError(t('onboarding.error'))
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5" noValidate>
      <div className="mb-2">
        <h2 className="text-xl font-semibold text-neutral-900">{t('onboarding.step1.title')}</h2>
        <p className="mt-1 text-sm text-neutral-600">{t('onboarding.step1.subtitle')}</p>
      </div>

      {apiError && <Alert variant="error">{apiError}</Alert>}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          label={t('onboarding.step1.first_name')}
          placeholder={t('onboarding.step1.first_name_placeholder')}
          autoComplete="given-name"
          error={errors.first_name?.message && t(errors.first_name.message)}
          {...register('first_name')}
        />
        <Input
          label={t('onboarding.step1.last_name')}
          placeholder={t('onboarding.step1.last_name_placeholder')}
          autoComplete="family-name"
          error={errors.last_name?.message && t(errors.last_name.message)}
          {...register('last_name')}
        />
      </div>

      <Input
        label={t('onboarding.step1.date_of_birth')}
        type="date"
        max={today}
        error={errors.date_of_birth?.message && t(errors.date_of_birth.message)}
        {...register('date_of_birth')}
      />

      <div className="flex flex-col gap-1">
        <label htmlFor="language" className="text-sm font-medium text-neutral-800">{t('onboarding.step1.language')}</label>
        <select
          id="language"
          {...register('language')}
          className="min-h-[44px] rounded-md border border-surface-200 bg-white px-3 py-2 text-sm text-neutral-900 focus:border-mindeia-primary focus:outline-none focus:ring-2 focus:ring-mindeia-primary"
        >
          {LANGUAGES.map((lang) => (
            <option key={lang.value} value={lang.value}>
              {lang.label}
            </option>
          ))}
        </select>
        {errors.language && (
          <p role="alert" className="text-xs text-mindeia-error">
            {t(errors.language.message ?? '')}
          </p>
        )}
      </div>

      <Button type="submit" isLoading={isSubmitting} size="lg" className="w-full mt-2">
        {t('onboarding.step1.submit')}
      </Button>
    </form>
  )
}
